import axios from "axios";
import { useDispatch } from "react-redux";
import { useLoading } from "../components/LoadingContext";
import { showErrorToast } from "../components/Toast";
import { clearUserInfo } from "../store/authSlice";
import { persistor } from "../store/store";
import { getUserId, readAuthFromCookies } from "../utilities/authUtils";

let inProcessRequests = 0;
let csrfTokenPromise: Promise<string> | null = null;

const resolveApiBaseUrl = (): string => {
  const rawUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BASE_URL;
  if (!rawUrl) {
    throw new Error("[apiService] REACT_APP_API_BASE_URL is not set. Check your .env file.");
  }
  return rawUrl.endsWith("/") ? rawUrl : `${rawUrl}/`;
};

const useInterceptor = () => {
  const dispatch = useDispatch();
  const { setLoading } = useLoading();

  axios.defaults.headers.common["Content-Type"] = "application/json";
  axios.defaults.headers.common.Accept = "application/json";
  axios.defaults.baseURL = resolveApiBaseUrl();
  axios.defaults.withCredentials = true;

  // ✅ Request interceptor
  axios.interceptors.request.use(
    async (request) => {
      if (
        request.url &&
        !request.url.startsWith("/") &&
        !/^https?:\/\//i.test(request.url)
      ) {
        request.url = `/${request.url}`;
      }

      if (!request.url?.includes("api/Auth/login")) {
        inProcessRequests++;
        // Try to resolve user_id; prefer localStorage, fallback to cookie-backed getter
        let userId = localStorage.getItem("user_id");
        if (!userId) {
          try {
            userId = await getUserId();
          } catch (e) {
            userId = null;
          }
        }

        // Tokens are read from cookies only — never from localStorage
        // JWT and x-auth must not be written to localStorage (XSS risk)
        if (userId) {
          const auth = readAuthFromCookies();
          if (auth?.jwt) request.headers.Authorization = `Bearer ${auth.jwt}`;
          if (auth?.pksoft) request.headers["X-AUTH"] = auth.pksoft;
        }

        // 🔒 Inject CSRF token only for unsafe requests
        if (
          ["post", "put", "delete"].includes(
            request.method?.toLowerCase() || "",
          )
        ) {
          // If a CSRF fetch is already in flight, reuse that promise
          if (!csrfTokenPromise) {
            csrfTokenPromise = axios
              .get("/api/pk/Customer/verify-cs", {
                withCredentials: true,
              })
              .then((res) => res.data.ott)
              .finally(() => {
                // Clear promise after a short delay to allow request batching
                setTimeout(() => {
                  csrfTokenPromise = null;
                }, 100);
              });
          }

          try {
            const csrfToken = await csrfTokenPromise;
            request.headers["X-CSRF-TOKEN"] = csrfToken;
          } catch (error) {
            csrfTokenPromise = null;
            console.error("Failed to fetch CSRF token", error);
            throw error;
          }
        }
      }

      setLoading(inProcessRequests > 0);
      return request;
    },
    (error) => {
      if (inProcessRequests === 0) setLoading(false);
      return Promise.reject(error);
    },
  );

  // ✅ Response interceptor
  axios.interceptors.response.use(
    (response) => {
      inProcessRequests = Math.max(inProcessRequests - 1, 0);
      if (inProcessRequests === 0) setLoading(false);

      return response;
    },
    (error) => {
      inProcessRequests = Math.max(inProcessRequests - 1, 0);
      if (inProcessRequests === 0) setLoading(false);

      if (error.response?.status === 401) {
        showErrorToast("Your session has expired. Please log in again.");
        csrfTokenPromise = null;
        setTimeout(() => {
          dispatch(clearUserInfo());
          localStorage.removeItem("JWT");
          localStorage.removeItem("x-auth");
          persistor.purge();
          window.location.href = "/";
        }, 2000);
        return;
      }

      if (error.response?.status === 403) {
        csrfTokenPromise = null;
      }

      if (error.code === "ERR_NETWORK") {
        showErrorToast("Network error - please check your connection.");
        console.error(error);
      }

      return Promise.reject(error);
    },
  );
};

export default useInterceptor;
